import { alpha, SxProps, useTheme } from '@mui/material';

import { colors, COLORS } from '@constants/colors';
import { ScreenSizes } from '@interfaces';
import { useMytaverse } from '@providers/MytaverseProvider';

import { HELP_GUIDE_MONO_FONT } from './constants';

export const useStyles = () => {
  const { breakpoints } = useTheme();
  const { appTheme } = useMytaverse();
  const accent = appTheme?.mainColor ?? COLORS.MYTAVERSE_BLUE;

  return {
    root: {
      zIndex: 10003,

      '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(8, 5, 12, 0.5)',
        backdropFilter: 'blur(6px)',
      },
    },
    container: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      flexDirection: 'column',
      width: 860,
      maxWidth: '94dvw',
      maxHeight: '90dvh',
      overflow: 'hidden',
      backgroundColor: 'rgba(12, 11, 11, 0.86)',
      backdropFilter: 'blur(24px)',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      borderRadius: 6,
      boxShadow: '0px 30px 80px rgba(0, 0, 0, 0.55)',

      '&:focus-visible': {
        outline: 'none',
      },

      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        width: '94dvw',
      },

      [breakpoints.down(ScreenSizes.Tablet)]: {
        width: '96dvw',
        maxHeight: '92dvh',
        borderRadius: 4,
      },
    } as SxProps,
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      paddingX: 3,
      paddingTop: 2.5,
      paddingBottom: 2,
      borderBottom: '1px solid ' + colors.white_10,
      flexShrink: 0,
    },
    headerLabel: {
      fontFamily: HELP_GUIDE_MONO_FONT,
      fontSize: 10,
      lineHeight: 1.6,
      letterSpacing: '0.34em',
      textTransform: 'uppercase',
      color: colors.white_40,
    },
    headerTitle: {
      fontFamily: 'var(--primary-font-bold)',
      fontSize: 19,
      lineHeight: 1.25,
      color: colors.white,
    },
    closeButton: {
      marginLeft: 'auto',
      width: 34,
      height: 34,
      color: colors.white_60,
      border: '1px solid rgba(255, 255, 255, 0.15)',

      '&:hover': {
        color: colors.white,
        backgroundColor: colors.white_10,
      },

      '& svg': {
        fontSize: 18,
      },
    },
    body: {
      display: 'flex',
      flex: 1,
      minHeight: 404,
      overflow: 'hidden',

      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'auto',
      },
    } as SxProps,
    rail: {
      width: 224,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      padding: 1.75,
      borderRight: '1px solid ' + colors.white_10,
      backgroundColor: 'rgba(255, 255, 255, 0.02)',

      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        width: '100%',
        flexDirection: 'row',
        overflowX: 'auto',
        borderRight: 0,
        borderBottom: '1px solid ' + colors.white_10,
      },
    } as SxProps,
    railTab: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 1.5,
      width: '100%',
      paddingX: 1.75,
      paddingY: 1.5,
      borderRadius: 3,
      border: '1px solid transparent',
      fontSize: 13.5,
      lineHeight: 1.3,
      letterSpacing: '0.02em',
      textAlign: 'left',
      color: colors.white_60,

      '& svg': {
        fontSize: 18,
        opacity: 0.85,
        flexShrink: 0,
      },

      '&:hover': {
        color: colors.white,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
      },

      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        width: 'auto',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      },
    } as SxProps,
    railTabActive: {
      color: colors.white,
      backgroundColor: alpha(accent, 0.18),
      borderColor: alpha(accent, 0.5),

      '& svg': {
        opacity: 1,
      },

      '&:hover': {
        color: colors.white,
        backgroundColor: alpha(accent, 0.18),
      },
    },
    pane: {
      flex: 1,
      paddingX: 3.5,
      paddingY: 1,
      overflowY: 'auto',
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '176px 1fr',
      alignItems: 'center',
      gap: 3,
      paddingY: 2.5,
      borderBottom: '1px dashed rgba(255, 255, 255, 0.08)',

      '&:last-of-type': {
        borderBottom: 0,
      },

      [breakpoints.down(ScreenSizes.Tablet)]: {
        gridTemplateColumns: '96px 1fr',
        gap: 2,
      },
    } as SxProps,
    rowVisual: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    rowTitle: {
      fontSize: 15,
      fontWeight: 600,
      lineHeight: 1.3,
      color: colors.white,
      marginBottom: 0.5,
    },
    rowDescription: {
      fontSize: 12.8,
      lineHeight: 1.55,
      color: colors.white_60,
    },
    keyCap: {
      minWidth: 38,
      height: 38,
      paddingX: '10px',
      display: 'inline-grid',
      placeItems: 'center',
      borderRadius: '10px',
      fontFamily: HELP_GUIDE_MONO_FONT,
      fontWeight: 600,
      fontSize: 13,
      color: colors.white,
      background:
        'linear-gradient(180deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.03))',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      borderBottomColor: 'rgba(255, 255, 255, 0.1)',
      boxShadow:
        '0px 3px 0px rgba(0, 0, 0, 0.5), inset 0px 1px 0px rgba(255, 255, 255, 0.12)',
      userSelect: 'none',
    },
    keyCapAccent: {
      color: colors.white,
      borderColor: alpha(accent, 0.65),
      borderBottomColor: alpha(accent, 0.35),
      background:
        'linear-gradient(180deg, ' +
        alpha(accent, 0.24) +
        ', ' +
        alpha(accent, 0.08) +
        ')',
      boxShadow:
        '0px 3px 0px rgba(0, 0, 0, 0.5), 0px 0px 16px ' + alpha(accent, 0.25),
    },
    keyCluster: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, auto)',
      gap: '6px',
      justifyItems: 'center',

      '& > :nth-of-type(1)': {
        gridArea: '1 / 2',
      },
      '& > :nth-of-type(2)': {
        gridArea: '2 / 1',
      },
      '& > :nth-of-type(3)': {
        gridArea: '2 / 2',
      },
      '& > :nth-of-type(4)': {
        gridArea: '2 / 3',
      },
    },
    mouse: {
      position: 'relative',
      width: 30,
      height: 43,
      flexShrink: 0,
      borderRadius: '15px',
      border: '1.5px solid rgba(255, 255, 255, 0.55)',
      background:
        'linear-gradient(180deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.02))',
      boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.1)',

      '&::before': {
        content: '""',
        position: 'absolute',
        left: '50%',
        top: -1,
        transform: 'translateX(-50%)',
        width: '1.5px',
        height: '46%',
        backgroundColor: 'rgba(255, 255, 255, 0.55)',
      },

      '&::after': {
        content: '""',
        position: 'absolute',
        left: -1.5,
        top: -1.5,
        width: '53%',
        height: '48%',
        borderTopLeftRadius: '15px',
        borderBottomLeftRadius: '7px',
        borderTopRightRadius: '4px',
        background:
          'linear-gradient(180deg, ' +
          alpha(accent, 0.95) +
          ', ' +
          alpha(accent, 0.7) +
          ')',
        boxShadow: '0px 0px 14px ' + alpha(accent, 0.45),
      },
    },
    iconRing: {
      width: 40,
      height: 40,
      flexShrink: 0,
      borderRadius: '50%',
      border: '1px solid ' + alpha(accent, 0.5),
      backgroundColor: alpha(accent, 0.14),
      display: 'grid',
      placeItems: 'center',
      color: colors.white,

      '& svg': {
        fontSize: 20,
      },
    },
    hotkeyGroup: {
      paddingBottom: 1,
    },
    hotkeyGroupLabel: {
      fontFamily: HELP_GUIDE_MONO_FONT,
      fontSize: 10,
      lineHeight: 1.6,
      letterSpacing: '0.3em',
      textTransform: 'uppercase',
      color: colors.white_40,
      paddingTop: 2,
      paddingBottom: 0.5,
    },
    hotkeyGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      columnGap: 4,

      [breakpoints.down(ScreenSizes.Tablet)]: {
        gridTemplateColumns: '1fr',
      },
    } as SxProps,
    hotkeyRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      paddingY: 1,
      borderBottom: '1px dashed rgba(255, 255, 255, 0.08)',
    },
    hotkeyLabel: {
      fontSize: 13.5,
      lineHeight: 1.3,
      color: colors.white,
    },
    hotkeyDescription: {
      fontSize: 11.5,
      lineHeight: 1.4,
      color: colors.white_60,
      marginTop: '1px',
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      gap: 1.75,
      paddingX: 3,
      paddingY: 2,
      borderTop: '1px solid ' + colors.white_10,
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
      flexShrink: 0,
    },
    tipBadge: {
      fontFamily: HELP_GUIDE_MONO_FONT,
      fontSize: 10,
      fontWeight: 700,
      lineHeight: 1,
      letterSpacing: '0.3em',
      textTransform: 'uppercase',
      color: colors.white,
      border: '1px solid ' + alpha(accent, 0.55),
      backgroundColor: alpha(accent, 0.2),
      borderRadius: '6px',
      padding: '6px 5px 5px 9px',
      flexShrink: 0,
    },
    tipText: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      fontSize: 12.5,
      color: colors.white_60,
    },
    inlineKey: {
      display: 'inline-grid',
      placeItems: 'center',
      minWidth: 20,
      padding: '2px 6px',
      borderRadius: '5px',
      border: '1px solid ' + alpha(accent, 0.5),
      backgroundColor: alpha(accent, 0.15),
      fontFamily: HELP_GUIDE_MONO_FONT,
      fontWeight: 600,
      fontSize: 11,
      lineHeight: 1.4,
      color: colors.white,
    },
    footerHint: {
      marginLeft: 'auto',
      fontFamily: HELP_GUIDE_MONO_FONT,
      fontSize: 10,
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      color: colors.white_40,
    },
  };
};
